import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Deliverables.module.scss';
import DeliverableTable from './components/DeliverableTable';
import { DeliverObject } from '../../api/model/deliverObject';
import { DeliverObjectDto, ProjectDto } from '../../api/model';
import { IconPlus, IconFilterFilled, IconX, IconChevronDown, IconSearch } from '@tabler/icons-react';
import CreateDeliverablePopup from './components/CreateDeliverablePopup';
import DeliverableDetailPopup from './components/DeliverableDetailPopup';
import { useProjectDeliverables } from '../../hooks/useProjectDeliverables';
import { useProjectPhases } from '../../hooks/useProjectPhases';
import { Deliverable } from '../../types/deliverables';
import { useQueryClient } from '@tanstack/react-query';
import {useGetLabels} from '../../api/label-api/label-api';
import {isAgileArtefactDeliverable} from '../../utils/phaseUtils';
import { useGetProjectIdMembers } from '../../api/project-api/project-api';
import { deliverableTableRows, deliverablesWithPhase } from './deliverableRows';

interface DeliverablesProps {
	project?: ProjectDto;
	onNavigateToTask?: (taskId: number) => void;
}

const convertToCalendarFormat = (deliverable: DeliverObjectDto): DeliverObject => {
	return {
		id: deliverable.id,
		group: deliverable.name || 'Unnamed Deliverable',
		startDate: deliverable.startDate,
		endDate: deliverable.endDate,
		criticality: deliverable.criticality,
		checkPlan: undefined
	};
};

const Deliverables: React.FC<DeliverablesProps> = ({
	project,
	onNavigateToTask,
}) => {
	const { t } = useTranslation('deliverables');

	const [itemsPerPage, setItemsPerPage] = useState(8);
	const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
	const [selectedDeliverable, setSelectedDeliverable] = useState<Deliverable | null>(null);
	const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
	const [nameFilter, setNameFilter] = useState('');
	const [criticalityFilter, setCriticalityFilter] = useState<string[]>([]);
	const [phaseFilter, setPhaseFilter] = useState<string[]>([]);
	const [isCriticalityDropdownOpen, setIsCriticalityDropdownOpen] = useState(false);
	const [isPhaseDropdownOpen, setIsPhaseDropdownOpen] = useState(false);
    const [labelFilter, setLabelFilter] = useState<string[]>([]);
    const [isLabelDropdownOpen, setIsLabelDropdownOpen] = useState(false);
    const [showAgileArtefacts, setShowAgileArtefacts] = useState(false);

    const {data: allLabels = []} = useGetLabels(
        {projectId: project?.id},
        {query: {enabled: !!project?.id}}
    );

	// Reference for dropdown click outside handling
	const filterPanelRef = useRef<HTMLDivElement>(null);

	// Handle click outside filter popup to close it
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
                filterPanelRef.current &&
				!filterPanelRef.current.contains(event.target as Node)
			) {
				setIsFilterPanelOpen(false);
				setIsCriticalityDropdownOpen(false);
				setIsPhaseDropdownOpen(false);
                setIsLabelDropdownOpen(false);
			}
		};
		
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);
	
	// Handle criticality filter toggle
	const handleCriticalityChange = (criticality: string) => {
		setCriticalityFilter(prev => {
			const uppercaseCriticality = criticality.toUpperCase();
			if (prev.includes(uppercaseCriticality)) {
				return prev.filter(item => item !== uppercaseCriticality);
			} else {
				return [...prev, uppercaseCriticality];
			}
		});
	};
	
	// Handle phase filter toggle
	const handlePhaseChange = (phase: string) => {
		setPhaseFilter(prev => {
			if (prev.includes(phase)) {
				return prev.filter(item => item !== phase);
			} else {
				return [...prev, phase];
			}
		});
	};
	
	// Get the QueryClient
	const queryClient = useQueryClient();

	// fetch deliverables and filter by phases
	const { 
		deliverables: projectDeliverables, 
		isLoading: isDeliverablesLoading, 
		error: deliverablesError,
		hasData 
	} = useProjectDeliverables(project);

	// Get phases for current project
	const {
		data: projectPhases = [],
		isLoading: isPhasesLoading
	} = useProjectPhases(project?.id);
	
	// Process project deliverables for table and calendar display
	const allDeliverables = useMemo(
		() => deliverablesWithPhase(projectDeliverables, projectPhases),
		[projectDeliverables, projectPhases],
	);

	// Convert to table format for DeliverableTable component
	const { data: projectMembers } = useGetProjectIdMembers(project?.id ?? 0, {
		query: { enabled: !!project?.id },
	});

	const tableDeliverables = useMemo(
		() => deliverableTableRows(projectDeliverables, projectPhases, projectMembers),
		[projectDeliverables, projectPhases, projectMembers],
	);

	const calendarDeliverables = useMemo(() => {
		return allDeliverables
			.map(({ deliverable }) => convertToCalendarFormat(deliverable))
			.filter(del => del.startDate && del.endDate); 
	}, [allDeliverables]);

	const finalTableData = useMemo(() => {
		if (showAgileArtefacts) return tableDeliverables;
		return tableDeliverables.filter(
            (d) => !isAgileArtefactDeliverable({name: d.name})
		);
	}, [tableDeliverables, showAgileArtefacts]);

	
	// Get available phases
	const availablePhases = useMemo(() => {
		const phases = new Set<string>();
		const phaseOrder: string[] = [];
		tableDeliverables.forEach(deliverable => {
			if (deliverable.phase && !phases.has(deliverable.phase)) {
				phases.add(deliverable.phase);
				phaseOrder.push(deliverable.phase);
			}
		});
		return phaseOrder;
	}, [tableDeliverables]);
	return (
		<>
			<div className={styles.planHeader}>
				<div className={styles.planHeaderContent}>
				<h2 className={styles.planTitle}>{t('title')}</h2>
				<p className={styles.planDescription}>
						{t('description')}
						{!project && ' ' + t('selectProject')}
						{project && !hasData && !isDeliverablesLoading && !deliverablesError && ' ' + t('noData')}
						{deliverablesError ? ' ' + t('error') : ''}
						{isDeliverablesLoading && ' ' + t('loading')}
					</p>
				</div>
				<div className={styles.planActions}>
					<button 
						className={`${styles.filterButton} ${isFilterPanelOpen ? styles.active : ''} ${
                            (nameFilter || criticalityFilter.length > 0 || phaseFilter.length > 0 || labelFilter.length > 0) ? styles.filtersApplied : ''
						}`}
						onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
						aria-label="Filter deliverables"
					>
						<IconFilterFilled />
						<span className={styles.filterButtonText}>{t('filter')}</span>
                        {(nameFilter || criticalityFilter.length > 0 || phaseFilter.length > 0 || labelFilter.length > 0) &&
                            <span className={styles.filterBadge}></span>}
					</button>
					<button 
						className={styles.planButton}
						onClick={() => setIsCreatePopupOpen(true)}
					>
						<IconPlus size={18} />
						<span className={styles.planButtonText}>{t('addNew')}</span>
					</button>
					
					{isFilterPanelOpen && (
						<div className={styles.filterPanel} ref={filterPanelRef}>
							<div className={styles.filterSearchWrapper}>
								<div className={styles.searchInput}>
									<IconSearch size={16} className={styles.searchIcon} />
									<input
										type="text"
										placeholder={t('search')}
										value={nameFilter}
										onChange={(e) => setNameFilter(e.target.value)}
										className={styles.filterInput}
									/>
									{nameFilter && (
										<button 
											className={styles.clearButton} 
											onClick={() => setNameFilter('')}
											aria-label="Clear search"
										>
											<IconX size={14} />
										</button>
									)}
								</div>
							</div>
							
							<div className={styles.filterCriticalityWrapper}>
								<div className={styles.dropdownContainer}>
									<button 
										className={styles.dropdownButton} 
										onClick={() => {
											setIsCriticalityDropdownOpen(!isCriticalityDropdownOpen);
											setIsPhaseDropdownOpen(false);
										}}
									>
										{criticalityFilter.length > 0 
											? `${criticalityFilter.length} criticality selected` 
											: t('filterCriticality')}
										<IconChevronDown size={16} className={`${styles.dropdownIcon} ${isCriticalityDropdownOpen ? styles.rotate : ''}`} />
									</button>
									
									{isCriticalityDropdownOpen && (
										<div className={styles.dropdown}>
											<div className={styles.dropdownItem}>
												<label className={styles.checkboxLabel}>
													<input 
														type="checkbox"
														checked={criticalityFilter.includes('HIGH')}
														onChange={() => handleCriticalityChange('HIGH')}
														className={styles.checkbox}
													/>
													<span className={`${styles.criticalityDot} ${styles.high}`}></span>
{t("criticality.high")}
												</label>
											</div>
											<div className={styles.dropdownItem}>
												<label className={styles.checkboxLabel}>
													<input 
														type="checkbox"
														checked={criticalityFilter.includes('MIDDLE')}
														onChange={() => handleCriticalityChange('MIDDLE')}
														className={styles.checkbox}
													/>
													<span className={`${styles.criticalityDot} ${styles.middle}`}></span>
{t("criticality.middle")}
												</label>
											</div>
											<div className={styles.dropdownItem}>
												<label className={styles.checkboxLabel}>
													<input 
														type="checkbox"
														checked={criticalityFilter.includes('EASY')}
														onChange={() => handleCriticalityChange('EASY')}
														className={styles.checkbox}
													/>
													<span className={`${styles.criticalityDot} ${styles.easy}`}></span>
{t("criticality.easy")}
												</label>
											</div>
											<div className={styles.dropdownItem}>
												<label className={styles.checkboxLabel}>
													<input 
														type="checkbox"
														checked={criticalityFilter.includes('NULL')}
														onChange={() => handleCriticalityChange('NULL')}
														className={styles.checkbox}
													/>
													<span className={`${styles.criticalityDot} ${styles['not-assigned']}`}></span>
{t("criticality.notAssigned")}
												</label>
											</div>
										</div>
									)}
								</div>
							</div>
							
							<div className={styles.filterPhaseWrapper}>
								<div className={styles.dropdownContainer}>
                                    <button
                                        className={styles.dropdownButton}
										onClick={() => {
											setIsPhaseDropdownOpen(!isPhaseDropdownOpen);
											setIsCriticalityDropdownOpen(false);
                                            setIsLabelDropdownOpen(false);
										}}
									>
                                        {phaseFilter.length > 0
                                            ? `${phaseFilter.length} phase selected`
										: t('filterPhase')}
										<IconChevronDown size={16} className={`${styles.dropdownIcon} ${isPhaseDropdownOpen ? styles.rotate : ''}`} />
									</button>

									{isPhaseDropdownOpen && (
										<div className={styles.dropdown}>
											{availablePhases.length > 0 ? (
												availablePhases.map(phase => (
													<div key={phase} className={styles.dropdownItem}>
														<label className={styles.checkboxLabel}>
                                                            <input
																type="checkbox"
																checked={phaseFilter.includes(phase)}
																onChange={() => handlePhaseChange(phase)}
																className={styles.checkbox}
															/>
															{phase}
														</label>
													</div>
												))
											) : (
												<div className={styles.dropdownItem}>
													<span className={styles.emptyMessage}>No phases available</span>
												</div>
											)}
										</div>
									)}
								</div>
							</div>

                            <div className={styles.filterPhaseWrapper}>
                                <div className={styles.dropdownContainer}>
                                    <button
                                        className={styles.dropdownButton}
                                        onClick={() => {
                                            setIsLabelDropdownOpen(!isLabelDropdownOpen);
                                            setIsCriticalityDropdownOpen(false);
                                            setIsPhaseDropdownOpen(false);
                                        }}
                                    >
                                        {labelFilter.length > 0
                                            ? `${labelFilter.length} label selected`
                                            : 'Filter by label'}
                                        <IconChevronDown size={16}
                                                         className={`${styles.dropdownIcon} ${isLabelDropdownOpen ? styles.rotate : ''}`}/>
                                    </button>

                                    {isLabelDropdownOpen && (
                                        <div className={styles.dropdown}>
                                            {allLabels.length > 0 ? (
                                                allLabels.map(label => (
                                                    <div key={label.id} className={styles.dropdownItem}>
                                                        <label className={styles.checkboxLabel}>
                                                            <input
                                                                type="checkbox"
                                                                checked={labelFilter.includes(label.id!.toString())}
                                                                onChange={() => {
                                                                    const id = label.id!.toString();
                                                                    setLabelFilter(prev =>
                                                                        prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id]
                                                                    );
                                                                }}
                                                                className={styles.checkbox}
                                                            />
                                                            <span
                                                                style={{
                                                                    width: 10,
                                                                    height: 10,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: label.color || '#e5e7eb',
                                                                    flexShrink: 0,
                                                                    display: 'inline-block'
                                                                }}
                                                            />
                                                            {label.name}
                                                        </label>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className={styles.dropdownItem}>
                                                    <span className={styles.emptyMessage}>No labels available</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

							<div className={styles.filterAgileWrapper}>
								<label className={styles.agileCheckboxLabel}>
									<input
										type="checkbox"
										checked={showAgileArtefacts}
										onChange={(e) => setShowAgileArtefacts(e.target.checked)}
										className={styles.checkbox}
									/>
									Show Agile Artefacts
								</label>
							</div>
						</div>
					)}
				</div>
			</div>
			
			<div className={styles.tableWrapper}>
				{isDeliverablesLoading ? (
					<div className={styles.loadingIndicator}>Loading deliverables...</div>
				) : deliverablesError ? (
					<div className={styles.errorIndicator}>
						<p>Error loading deliverables. Please try refreshing the page.</p>
						<button onClick={() => window.location.reload()} className={styles.retryButton}>
							Retry
						</button>
					</div>
				) : !project ? (
					<div className={styles.emptyState}>
						<p>Please select a project to view deliverables.</p>
					</div>
				) : finalTableData.length === 0 ? (
					<div className={styles.emptyState}>
						<p>No deliverables found for this project.</p>
						<p>Create your first deliverable by clicking "Add New Item" above.</p>
					</div>
				) : (
                    <DeliverableTable
						deliverables={finalTableData}
						itemsPerPage={itemsPerPage}
						onItemsPerPageChange={setItemsPerPage}
						onDeliverableClick={(deliverable) => setSelectedDeliverable(deliverable)}
						deliverableHref={(deliverable) => `/project/${project?.id}?section=deliverables&deliverableId=${deliverable.id}`}
						nameFilter={nameFilter}
						criticalityFilter={criticalityFilter}
						phaseFilter={phaseFilter}
                        labelFilter={labelFilter}
					/>
				)}
			</div>

			
			<CreateDeliverablePopup 
				isOpen={isCreatePopupOpen}
				onClose={() => setIsCreatePopupOpen(false)}
				phases={projectPhases}
				projectId={project?.id}
				project={project}
			/>
			{selectedDeliverable && (
				<DeliverableDetailPopup
					isOpen={true}
					onClose={() => setSelectedDeliverable(null)}
					deliverable={selectedDeliverable}
					projectDeliverables={tableDeliverables}
					rawDeliverables={projectDeliverables}
					projectId={project?.id}
					project={project}
					onNavigateToTask={onNavigateToTask}
					onDeliverableUpdate={(updatedDeliverable) => {
					// Manually refresh deliverables after an update
					queryClient.invalidateQueries({ queryKey: ['/deliver-object'] });
					
					// Also refresh phase specific things
					if (project?.id) {
						queryClient.invalidateQueries({ queryKey: [`/phase/${project.id}/project`] });
					}
					const deliverableAny = updatedDeliverable as any;
					const phaseId = deliverableAny.phase_id || deliverableAny.phaseId;
					if (phaseId) {
						queryClient.invalidateQueries({ queryKey: [`/deliver-object/${phaseId}/phase`] });
					}
				}}
				/>
			)}
		</>
	);
};

export default Deliverables;
